import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@HoudiniUI/Select';
import * as Api from '@ViewModels';
import React from 'react';
import { EditModeCombobox, EditModeSelect } from '../context';
import { getProviderConfig } from '../providers';
import { getAvailableFields, getFieldsDisplayValue, getSettingValue, recordToItems } from '../providers/utils';
import { styleSheet } from '../styles';
import { SettingGroup } from './SettingGroup';
import { SettingRow } from './SettingRow';

interface IKeyFactSettingsGroupProps {
	keyFactSettings: Api.IKeyFactSettings;
	configuration: Api.IIntegrationConfiguration;
	provider: Api.IntegrationProvider;
	setKeyFactsSettings: (keyFactsSettings: Api.IKeyFactSettings) => void;
}

const birthdayDisplayOptions = [
	{ label: 'All', value: Api.BirthdayKeyFactDisplay.All },
	{ label: 'Clients Only', value: Api.BirthdayKeyFactDisplay.ClientOnly },
	{ label: 'Prospects Only', value: Api.BirthdayKeyFactDisplay.ProspectOnly },
];

export const KeyFactSettingsGroup: React.FC<IKeyFactSettingsGroupProps> = ({
	keyFactSettings,
	configuration,
	provider,
	setKeyFactsSettings,
}) => {
	const providerConfig = getProviderConfig(provider);
	const keyFactsSchema = providerConfig.keyFacts;

	const onUpdate = (updatedSettings: Partial<Api.IKeyFactSettings>) => {
		setKeyFactsSettings({
			...keyFactSettings,
			...updatedSettings,
		});
	};

	// Render date fields section if configured
	const renderDateFields = () => {
		if (!keyFactsSchema?.dateFields) return null;

		const { availableFrom, settingsKey, label, subtitle } = keyFactsSchema.dateFields;
		const availableFields = getAvailableFields(configuration, availableFrom);
		const enabledFields = getSettingValue<string[]>(keyFactSettings, settingsKey) ?? [];

		if (!Object.keys(availableFields).length) return null;

		return (
			<SettingRow title={label} subtitle={subtitle}>
				<EditModeCombobox
					multiple
					items={recordToItems(availableFields)}
					value={enabledFields}
					onChange={(selectedFields: string[]) => onUpdate({ [settingsKey]: selectedFields })}
					displayValue={getFieldsDisplayValue(enabledFields, availableFields)}
					searchPlaceholder={`Search ${label.toLowerCase()}...`}
				/>
			</SettingRow>
		);
	};

	// Render custom date fields section if configured
	const renderCustomDateFields = () => {
		if (!keyFactsSchema?.customDateFields) return null;

		const { availableFrom, settingsKey, label, subtitle } = keyFactsSchema.customDateFields;
		const availableFields = getAvailableFields(configuration, availableFrom);
		const enabledFields = getSettingValue<string[]>(keyFactSettings, settingsKey) ?? [];

		if (!Object.keys(availableFields).length) return null;

		return (
			<SettingRow title={label} subtitle={subtitle}>
				<EditModeCombobox
					multiple
					items={recordToItems(availableFields)}
					value={enabledFields}
					onChange={(selectedFields: string[]) => onUpdate({ [settingsKey]: selectedFields })}
					displayValue={getFieldsDisplayValue(enabledFields, availableFields)}
					searchPlaceholder={`Search ${label.toLowerCase()}...`}
				/>
			</SettingRow>
		);
	};

	return (
		<SettingGroup title='Key Facts Settings'>
			{keyFactsSchema?.showBirthdayDisplay && (
				<SettingRow title='Birthday Display' subtitle='Determines type of contact to show birthdays for'>
					<EditModeSelect
						items={birthdayDisplayOptions}
						value={keyFactSettings?.birthdayDisplay ?? null}
						onValueChange={(value: string) => onUpdate({ birthdayDisplay: value as Api.BirthdayKeyFactDisplay })}
						displayValue={keyFactSettings?.birthdayDisplay ?? 'Select birthday display'}
					>
						<SelectTrigger styles={styleSheet.editableField}>
							<SelectValue placeholder='Select birthday display' />
						</SelectTrigger>
						<SelectContent>
							{birthdayDisplayOptions.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</EditModeSelect>
				</SettingRow>
			)}

			{renderDateFields()}
			{renderCustomDateFields()}
		</SettingGroup>
	);
};
